import React, {
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import PropTypes from 'prop-types';

import {
  fetchUsers,
  getAdminConstructionApiUrl,
  Pagination,
} from '@adsk/acs-sc-web-platform-api';
import { useGetAllPaginatedData, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { getOrCreateGlobalContext } from '@adsk/alloy-react-context';

import { useCurrentProjectId } from './CurrentProjectIdContext';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';

type FetchUsersResponse =
  ReturnType<typeof fetchUsers> extends Promise<infer T> ? T : never;

type User = FetchUsersResponse['results'] extends (infer T)[] ? T : never;

type DeletedUsersResponse = {
  pagination: Pagination;
  results: User[];
};

type DeletedUsersContextValue = {
  data: User[];
  loading: boolean;
  error: null | unknown;
  retry(): void;
  setEnabled?: (enabled: boolean) => void;
};

/**
 * Context for users list.
 */
const DeletedUsersContext = getOrCreateGlobalContext<DeletedUsersContextValue>(
  CONTEXT_NAMES.DeletedUsersContext,
);

const PAGE_LIMIT = 200;

type UseDeletedUsersOptionsType = {
  disable?: boolean;
};

const useDeletedUsersContext = () => {
  const contextValue = useContext(DeletedUsersContext);
  const hook = 'useDeletedUsers';
  const provider = 'DeletedUsersProvider';

  if (!contextValue) {
    console.error(`${hook} must be used within a ${provider}`);
  }

  return contextValue || {};
};

export const useDeletedUsers = (options?: UseDeletedUsersOptionsType) => {
  const { setEnabled, ...rest } = useDeletedUsersContext();
  const shouldFetchDeletedUsers = !options?.disable;

  useEffect(() => {
    setEnabled && setEnabled(shouldFetchDeletedUsers);
  }, [shouldFetchDeletedUsers, setEnabled]);

  return { ...rest, loading: options?.disable ? false : rest.loading };
};

type DeletedUsersProviderProps = {
  children?: ReactNode;
};

export const DeletedUsersProvider: FC<DeletedUsersProviderProps> = ({
  children,
}) => {
  const [enabled, setEnabled] = useState(false);
  const projectId = useCurrentProjectId();
  const adminApiUrl = getAdminConstructionApiUrl();

  const url =
    enabled && projectId
      ? `${adminApiUrl}/v1/projects/${projectId}/users?filter[status]=deleted&limit=${PAGE_LIMIT}`
      : '';

  const {
    loading: loadingData,
    error,
    data: dataFromBE,
    retry,
  } = useGetAllPaginatedData<DeletedUsersResponse>({
    url,
  });

  const data = useMemo(() => {
    return (
      dataFromBE &&
      removeOldDuplicatesBy(
        dataFromBE.flatMap((usersPages) => usersPages.results),
        (item) => item.id,
      )
    );
  }, [dataFromBE]);

  const loading = loadingData || !enabled;

  const value = useMemo(
    () => ({
      data,
      loading,
      error,
      retry,
      setEnabled: enabled ? undefined : setEnabled,
    }),
    [data, loading, error, retry, enabled],
  );

  return (
    <DeletedUsersContext.Provider value={value}>
      {children}
    </DeletedUsersContext.Provider>
  );
};

DeletedUsersProvider.displayName = 'DeletedUsersProvider';

DeletedUsersProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node,
};
